import React, { useState, useEffect } from 'react';
import { Menu, X, MessageCircle } from 'lucide-react';
import { images, companyInfo } from '../data/mockData';

const ModernHeader = ({ content, language, setLanguage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const languages = [
    { code: 'en', flag: '🇬🇧', label: 'EN' },
    { code: 'tr', flag: '🇹🇷', label: 'TR' },
    { code: 'pl', flag: '🇵🇱', label: 'PL' }
  ];

  const currentLang = languages.find(l => l.code === language);

  const handleLanguageChange = (code) => {
    setLanguage(code);
    localStorage.setItem('preferredLanguage', code);
    setShowLangMenu(false);
  };

  return (
    <header
      className={`fixed top-4 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'top-2' : ''
      }`}
    >
      <nav className="container mx-auto px-4 md:px-6">
        <div
          className={`bg-white/90 backdrop-blur-xl ${isMobileMenuOpen ? 'rounded-2xl' : 'rounded-full'} shadow-lg border border-slate-200/60 transition-all duration-500 ${
            isScrolled ? 'shadow-2xl' : ''
          }`}
        >
          <div className="flex items-center justify-between px-4 md:px-6 py-3">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src={images.logo}
                alt="Lamazi Group"
                className="h-12 md:h-14 w-auto object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-2">
              <button
                onClick={() => scrollToSection('home')}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200"
              >
                {content.nav.home}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200"
              >
                {content.nav.about}
              </button>
              <button
                onClick={() => scrollToSection('products')}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200"
              >
                {content.nav.products}
              </button>
              <button
                onClick={() => scrollToSection('packaging')}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200"
              >
                {content.nav.packaging}
              </button>
              <button
                onClick={() => scrollToSection('logistics')}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200"
              >
                {content.nav.logistics}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200"
              >
                {content.nav.contact}
              </button>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              {/* Language Switcher */}
              <div className="relative hidden md:block">
                <button
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all duration-200"
                >
                  <span className="text-lg">{currentLang.flag}</span>
                  <span>{currentLang.label}</span>
                </button>

                {/* Language Dropdown */}
                {showLangMenu && (
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-200 py-2 min-w-[140px]">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-emerald-50 transition-colors ${
                          language === lang.code ? 'bg-emerald-50 text-emerald-600' : 'text-slate-700'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="font-medium">{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* WhatsApp CTA Button */}
              <a
                href={`https://wa.me/${companyInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center space-x-2 px-5 py-2.5 bg-emerald-600 text-white rounded-full font-semibold text-sm hover:bg-emerald-700 transition-all duration-300 shadow-lg hover:shadow-emerald-600/50 hover:scale-105"
              >
                <MessageCircle className="w-4 h-4" />
                <span>{content.nav.whatsapp}</span>
              </a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-200 px-4 py-4 space-y-2">
              <button
                onClick={() => scrollToSection('home')}
                className="w-full text-left px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
              >
                {content.nav.home}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="w-full text-left px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
              >
                {content.nav.about}
              </button>
              <button
                onClick={() => scrollToSection('products')}
                className="w-full text-left px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
              >
                {content.nav.products}
              </button>
              <button
                onClick={() => scrollToSection('packaging')}
                className="w-full text-left px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
              >
                {content.nav.packaging}
              </button>
              <button
                onClick={() => scrollToSection('logistics')}
                className="w-full text-left px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
              >
                {content.nav.logistics}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="w-full text-left px-4 py-2 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
              >
                {content.nav.contact}
              </button>

              {/* Mobile Language Selector */}
              <div className="pt-2 border-t border-slate-200 space-y-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full flex items-center space-x-3 px-4 py-2 text-sm rounded-lg transition-all ${
                      language === lang.code
                        ? 'bg-emerald-50 text-emerald-600 font-semibold'
                        : 'text-slate-700 hover:bg-emerald-50'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>

              {/* Mobile WhatsApp Button */}
              <a
                href={`https://wa.me/${companyInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 w-full px-5 py-3 bg-emerald-600 text-white rounded-full font-semibold text-sm hover:bg-emerald-700 transition-all mt-4"
              >
                <MessageCircle className="w-5 h-5" />
                <span>{content.nav.whatsapp}</span>
              </a>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default ModernHeader;
